import { useState, useMemo } from 'react';
import { useYotpoProductBottomline } from '@hooks';
import { getMetafieldMap } from '@/utils/Metafields';
import { handleize } from '@/utils/Helper';
import EmptyStar from '../icons/EmptyStar.jsx';
import { YotpoStars } from '@/snippets/components/YotpoStars';
import { useCountry, useSettings } from '@backpackjs/storefront';
import { InfoPopup } from './InfoPopup.jsx';

function ProductInformation(props) {
  const { product, selectedVariant, selectedPlan, hideDescription, hideDiscount } = props;
  const { bottomline } = useYotpoProductBottomline({
    legacyResourceId: product?.legacyResourceId,
  });

  const metafieldMap = useMemo(() => {
    return getMetafieldMap(product);
  }, [product?.id]);

  const getMetafieldColor = () => {
    let metafieldColor = metafieldMap.custom_collection_title_color;
    if (metafieldColor) {
      metafieldColor = `text-${handleize(metafieldColor)}`;
    } else {
      metafieldColor = 'text-black';
    }

    return metafieldColor;
  };

  const siteSettings = useSettings();
  const collectionTitle = metafieldMap.custom_collection_title;
  const collectionUrl = metafieldMap.custom_collection_url;
  const collectionLinkActive = !!(collectionUrl && collectionTitle?.length);
  const ingredient = metafieldMap.custom_ingredient_title;
  const productTitle = metafieldMap.custom_product_title || product.title;
  const promo = metafieldMap.promo_message;
  const shortDescription = metafieldMap.short_description;

  const text = shortDescription;
  const textArray = text ? text.split(' ') : [];
  const [isReadMore, setIsReadMore] = useState(textArray.length > 20);

  const { country } = useCountry();
  const countryCode = country?.isoCode || 'US';
  const isUs = countryCode === 'US';
  let HasInfoPopup = false;
  let popupData = metafieldMap.getMetafieldsByNamespace('tooltip');

  if(popupData.tooltip_label != '' && popupData.body_content) {
    HasInfoPopup = true;
  }

  const toggleReadMore = () => {
    setIsReadMore((isReadMore) => {
      return !isReadMore;
    });
  };

  const buildProductTitle = () => {
    let title;
    if (props.LinkToProduct === true) {
      title = (
        <a
          href={`/products/${product.handle}`}
          className="md:h3 !mb-4 font-tiempos text-h2-m no-underline md:!mb-4"
          onClick={props.onTitleClick}
        >
          {productTitle}
        </a>
      );
    } else {
      title = (
        <p className="md:h3 !mb-4 font-tiempos text-h2-m md:!mb-4">
          {productTitle}
        </p>
      );
    }

    return title;
  };

  let compareValue = selectedVariant?.compareAtPriceV2?.amount || 0;
  let variantPrice = selectedVariant?.priceV2?.amount || 0;
  if (selectedPlan) {
    compareValue = selectedVariant?.compareAtPriceV2?.amount || selectedPlan?.priceAdjustments?.[0]?.compareAtPrice?.amount;
    variantPrice = selectedPlan?.priceAdjustments?.[0]?.price?.amount;
  }


  let discountValue = null;

  if (compareValue > 0) {
    discountValue = Math.floor(((compareValue - variantPrice) / compareValue) * 100);
  }

  const discountHighlight =
    siteSettings?.discountHighlight?.enableDiscountHighlight &&
    siteSettings?.discountHighlight?.pdpDiscountMessage &&
    discountValue !== 0 &&
    discountValue !== null;

  return (
    <div className="product-info-container--header mb-6 lg:relative">
      {!collectionLinkActive && !collectionTitle && (
        <span className="hidden h-3 lg:mb-4 lg:block" />
      )}
      {!collectionUrl && collectionTitle && (
        <p className={`sub-xsmall mb-2 md:mb-4 ${getMetafieldColor()}`}>
          {collectionTitle}
        </p>
      )}
      {collectionLinkActive && (
        <a
          href={collectionUrl}
          className={`sub-xsmall !mb-2 flex no-underline md:!mb-4 ${getMetafieldColor()}`}
        >
          {collectionTitle}
        </a>
      )}
      {ingredient && (
        <p className="mb-2 font-tiempos text-h3-m italic text-black md:mb-2 md:text-[28px]">
          {ingredient}
        </p>
      )}
      {productTitle && buildProductTitle()}
      <div className="yotpo-badge-container lg:absolute lg:top-0 lg:right-0 lg:bottom-0">
        {bottomline && bottomline.total_reviews > 0 ? (
          <a
            href="#pdp-yotpo-reviews-widget"
            className="flex flex-wrap items-center gap-1.5"
            aria-label="Scroll to product reviews"
          >
            {bottomline.total_reviews ? (
              <>
                <YotpoStars rating={bottomline.average_score} />
                <p className="mb-0 flex-1 text-[13px] leading-[17.5px] underline">
                  {bottomline.total_reviews} Review
                  {bottomline.total_reviews === 1 ? '' : 's'}
                </p>
              </>
            ) : (
              <p className="mb-0 text-[13px] leading-[17.5px] underline">
                No reviews
              </p>
            )}
          </a>
        ) : (
          <a
            href="#pdp-yotpo-reviews-widget"
            className="yotpo-no-reviews text-xsmall flex cursor-pointer items-center leading-none text-black underline"
          >
            <div className="mr-2 flex">
              <EmptyStar />
              <EmptyStar />
              <EmptyStar />
              <EmptyStar />
              <EmptyStar />
            </div>
            <span>Write a Review</span>
          </a>
        )}
      </div>
      {!hideDiscount && !discountHighlight && promo && isUs && (
        <p className="sub-xsmall md:sub-small text-pink product-promo-message mb-2">
          {promo}
        </p>
      )}
      {!hideDiscount && discountHighlight && (
        <p className="sub-xsmall md:sub-small text-pink product-discount-message mb-2">
          {siteSettings?.discountHighlight?.pdpDiscountMessage.replace(
            '[discount]',
            `${discountValue}%`
          )}
        </p>
      )}
      {hideDescription !== true && (
        <>
          <p className="text-xsmall md:text-small mb-2 flex flex-col text-black">
            {isReadMore ? textArray.slice(0, 20).join(' ') : text}
          </p>

          {textArray.length > 20 && (
            <p className="mt-2 mb-0">
              <button
                type="button"
                onClick={toggleReadMore}
                className="focus-link-no-link read-or-hide text-xsmall md:text-small inline cursor-pointer text-black"
              >
                {isReadMore ? 'read more' : 'read less'}
              </button>
            </p>
          )}
        </>
      )}
      {HasInfoPopup == true && (
        <InfoPopup settings={popupData} />
      )}
    </div>
  );
}

export default ProductInformation;
